import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { GlobalNotificationsService } from '@app/core/services/global-notifications/global-notifications.service';
import { Subscription } from 'rxjs';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { GlobalNotification, NotificationType } from '@app/core/services/global-notifications/global-notifications.model';
import { NotificationTypes } from '@app/core/helpers/constants';

@Component({
  selector: 'app-global-notifications-container',
  templateUrl: './global-notifications-container.component.html',
  styleUrl: './global-notifications-container.component.scss',
  animations: [
    trigger('notificationAnimation', [
      transition(':enter', [style({ opacity: 0, transform: 'scale(0.9)' }), animate('300ms ease-out', style({ opacity: 1, transform: 'scale(1)' }))]),
      transition(':leave', [
        animate(
          '300ms ease-in',
          style({
            opacity: 0,
            transform: 'scale(0.9)'
          })
        )
      ])
    ]),
    trigger('progressAnimation', [
      state('void', style({ width: '100%' })),
      state('start', style({ width: '100%' })),
      state('end', style({ width: '0%' })),
      transition('start => end', [animate('{{duration}}ms linear')])
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GlobalNotificationsContainerComponent implements OnInit, OnDestroy {
  notifications: GlobalNotification[] = [];
  processedNotifications: GlobalNotification[] = [];
  expandedStackTypes: Set<string> = new Set<string>();
  private processedIds: string[] = [];
  private initializedNotifications: Set<string> = new Set<string>();
  private subscription: Subscription;
  private notificationTimeouts: Map<string, number> = new Map();

  constructor(
    private notificationsService: GlobalNotificationsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.subscription = this.notificationsService.getNotifications().subscribe(notifications => {
      const previousIds = this.processedIds.slice();
      const previousNotifications = [...this.processedNotifications];

      this.notifications = notifications;
      this.processDisplayedNotifications();

      const currentIds = this.processedNotifications.filter(n => !n.isStacked).map(n => n.id);

      const newNotificationIds = currentIds.filter(id => !previousIds.includes(id) && !this.initializedNotifications.has(id));

      if (newNotificationIds.length > 0) {
        newNotificationIds.forEach(id => {
          const notification = this.notifications.find(n => n.id === id);
          if (notification && !notification.isPersistent) {
            this.initializedNotifications.add(id);
            this.startTemporaryNotificationTimer(notification);
          }
        });
      }

      const removedMainIds = previousIds.filter(id => !currentIds.includes(id));

      if (removedMainIds.length > 0) {
        this.handleRemovedNotifications();
      }

      this.processedIds = currentIds;
      this.cdr.markForCheck();
    });
  }

  public goToLink(link: string): void {
    window.open(link, '_blank');
  }

  private handleRemovedNotifications(): void {
    const nonStackedByType = new Map<string, GlobalNotification[]>();

    this.processedNotifications
      .filter(n => !n.isStacked)
      .forEach(n => {
        const key = `${n.isPersistent ? 'persistent' : 'temp'}_${n.type}`;
        if (!nonStackedByType.has(key)) {
          nonStackedByType.set(key, []);
        }
        nonStackedByType.get(key)?.push(n);
      });

    for (const [key, notifications] of nonStackedByType.entries()) {
      if (key.startsWith('temp_') && notifications.length === 1) {
        const notification = notifications[0];
        if (!this.initializedNotifications.has(notification.id)) {
          this.initializedNotifications.add(notification.id);
          this.startTemporaryNotificationTimer(notification);
        }
      }
    }
  }

  trackByNotification(index: number, notification: GlobalNotification): string {
    return notification.id;
  }

  private startTemporaryNotificationTimer(notification: GlobalNotification): void {
    if (notification.isPersistent) return;

    if (this.notificationTimeouts.has(notification.id)) {
      window.clearTimeout(this.notificationTimeouts.get(notification.id));
    }
    const duration = Math.max(notification.duration || 5000, 5000);
    let remainingTime = duration;
    let startTime = Date.now();

    this.notificationsService.updateNotificationState(notification.id, 'start');
    this.cdr.detectChanges();

    setTimeout(() => {
      this.notificationsService.updateNotificationState(notification.id, 'end');
      this.cdr.detectChanges();
    }, 50);

    const timeoutId = window.setTimeout(() => {
      const current = this.notifications.find(n => n.id === notification.id);
      if (current) {
        this.handleNotificationTimeout(notification.id);
        this.notificationTimeouts.delete(notification.id);
      }
    }, remainingTime);

    this.notificationTimeouts.set(notification.id, timeoutId);
  }

  private handleNotificationTimeout(id: string): void {
    const mainNotification = this.nonStackedNotifications.find(n => n.id === id && !n.isPersistent);
    if (!mainNotification) return;

    const type = mainNotification.type;
    const stackedNotifications = this.getStackedNotificationsForType(type, false);

    if (stackedNotifications.length > 0) {
      this.notificationsService.remove(id, false);

      const nextNotification = stackedNotifications[0];
      const originalId = this.getOriginalId(nextNotification.id);

      const notification = this.notifications.find(n => n.id === originalId);
      if (notification) {
        this.initializedNotifications.add(originalId);
        this.startTemporaryNotificationTimer(notification);
      }
    } else {
      this.notificationsService.remove(id, false);
    }
  }

  private processDisplayedNotifications(): void {
    const result: GlobalNotification[] = [];
    const tempByType: Record<string, GlobalNotification[]> = {};
    const persistentByType: Record<string, GlobalNotification[]> = {};

    // Grupowanie powiadomień po typie
    this.notifications.forEach(notification => {
      const type = notification.type;
      const collection = notification.isPersistent ? persistentByType : tempByType;
      if (!collection[type]) {
        collection[type] = [];
      }
      collection[type].push({ ...notification });
    });

    const processCollection = (typeCollection: Record<string, GlobalNotification[]>, isPersistent: boolean) => {
      Object.keys(typeCollection).forEach(type => {
        const sorted = [...typeCollection[type]].sort((a, b) => b.id.localeCompare(a.id));
        const key = `${isPersistent ? 'persistent' : 'temp'}_${type}`;

        if (sorted.length === 0) {
          this.expandedStackTypes.delete(key);
          return;
        }

        const mainNotification = {
          ...sorted[0],
          isStacked: false,
          isPersistent: sorted[0].isPersistent
        };

        result.push(mainNotification);

        if (sorted.length === 1) {
          this.expandedStackTypes.delete(key);
        }

        if (sorted.length > 1) {
          const stackedNotifications = sorted.slice(1).map((notification, idx) => ({
            ...notification,
            id: `${notification.id}_stacked`,
            isStacked: true,
            stackIndex: idx,
            animationState: notification.animationState,
            isPersistent: notification.isPersistent
          }));

          result.push(...stackedNotifications);

          if (!this.initializedNotifications.has(mainNotification.id) && !isPersistent) {
            this.initializedNotifications.add(mainNotification.id);
            this.startTemporaryNotificationTimer(mainNotification);
          }
        }
      });
    };

    processCollection(tempByType, false);
    processCollection(persistentByType, true);

    this.processedNotifications = this.sortNotificationsByTypePriority(result);
  }

  remove(id: string): void {
    const isStackedNotification = id.endsWith('_stacked');
    const originalId = this.getOriginalId(id);

    const notification = this.notifications.find(n => n.id === originalId);
    if (!notification) return;

    const processedNotification = this.processedNotifications.find(n => n.id === originalId || n.id === id);

    // console.log('processedNotifications', this.processedNotifications);
    // console.log('processedNotification', processedNotification);
    // console.log('notification', notification);
    // console.log('originalId', originalId);

    const isPersistent = notification.isPersistent && (processedNotification ? processedNotification.isPersistent : false);

    const type = notification.type;
    const key = `${isPersistent ? 'persistent' : 'temp'}_${type}`;

    const remainingNotifications = this.notifications.filter(n => n.id !== originalId && n.type === type && n.isPersistent === isPersistent);

    if (remainingNotifications.length <= 1) {
      // console.log(`Resetting stack expansion for ${key} as it will have ${remainingNotifications.length} notifications left`);
      this.expandedStackTypes.delete(key);
    }

    if (this.notificationTimeouts.has(originalId)) {
      window.clearTimeout(this.notificationTimeouts.get(originalId));
      this.notificationTimeouts.delete(originalId);
    }

    // console.log('Removing notification:', originalId,
    //   ' isPersistent:', isPersistent,
    //   ' original isPersistent:', notification.isPersistent,
    //   ' processed isPersistent:', processedNotification?.isPersistent
    // );
    this.notificationsService.remove(originalId, isPersistent);
  }

  removeAll(): void {
    this.notificationsService.removeAll();
  }

  checkNotificationIcon(type: NotificationType): string {
    let notificationIcon: string;
    switch (type) {
      case 'success':
        notificationIcon = 'check';
        break;
      case 'danger':
        notificationIcon = 'cross';
        break;
      case 'warning':
        notificationIcon = 'warning';
        break;
      case 'info':
        notificationIcon = 'info-2';
        break;
      default:
        notificationIcon = 'notifications';
    }
    return notificationIcon;
  }

  pauseNotification(notification: GlobalNotification): void {
    if (notification.isStacked || notification.isPersistent) return;

    this.notificationsService.updateNotificationState(notification.id, 'start');

    if (this.notificationTimeouts.has(notification.id)) {
      window.clearTimeout(this.notificationTimeouts.get(notification.id));
      this.notificationTimeouts.delete(notification.id);
    }

    this.cdr.markForCheck();
  }

  resumeNotification(notification: GlobalNotification): void {
    if (notification.isStacked || notification.isPersistent) return;

    setTimeout(() => {
      this.notificationsService.updateNotificationState(notification.id, 'end');

      const duration = Math.max(notification.duration || 5000, 5000);
      const timeoutId = window.setTimeout(() => {
        const current = this.notifications.find(n => n.id === notification.id);
        if (current) {
          this.handleNotificationTimeout(notification.id);
          this.notificationTimeouts.delete(notification.id);
        }
      }, duration);

      this.notificationTimeouts.set(notification.id, timeoutId);

      this.cdr.markForCheck();
    }, 50);
  }

  toggleStackExpansion(type: string, isPersistent: boolean = false): void {
    const key = `${isPersistent ? 'persistent' : 'temp'}_${type}`;
    const newSet = new Set(this.expandedStackTypes);
    newSet.add(key);
    this.expandedStackTypes = newSet;
    this.cdr.markForCheck();
  }

  isStackExpanded(type: string, isPersistent: boolean = false): boolean {
    const key = `${isPersistent ? 'persistent' : 'temp'}_${type}`;
    return this.expandedStackTypes.has(key);
  }

  getStackedNotificationTransform(notification: GlobalNotification): string {
    return 'none';
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.notificationTimeouts.forEach(timeoutId => {
      window.clearTimeout(timeoutId);
    });
    this.notificationTimeouts.clear();
  }

  get displayedNotifications(): GlobalNotification[] {
    return this.processedNotifications;
  }

  get nonStackedNotifications(): GlobalNotification[] {
    const notifications = this.displayedNotifications.filter(n => !n.isStacked);
    return notifications;
  }

  get stackedNotifications(): GlobalNotification[] {
    const notifications = this.displayedNotifications.filter(n => n.isStacked);
    return notifications;
  }

  hasStackedNotificationsForType(type: string, isPersistent: boolean = false): boolean {
    const stackedCount = this.stackedNotifications.filter(n => n.type === type && n.isPersistent === isPersistent).length;

    return stackedCount > 0;
  }

  getStackedNotificationsForType(type: string, isPersistent: boolean = false): GlobalNotification[] {
    const allStackedOfType = this.stackedNotifications.filter(n => n.type === type && n.isPersistent === isPersistent);

    const key = `${isPersistent ? 'persistent' : 'temp'}_${type}`;
    if (this.expandedStackTypes.has(key)) {
      return allStackedOfType;
    }

    return allStackedOfType.slice(0, 10);
  }

  getStackedCountForType(type: string, isPersistent: boolean = false): number {
    return this.stackedNotifications.filter(n => n.type === type && n.isPersistent === isPersistent).length;
  }

  private sortNotificationsByTypePriority(notifications: GlobalNotification[]): GlobalNotification[] {
    const visibleTemporary = notifications.filter(n => !n.isPersistent && !n.isStacked);
    const stackedTemporary = notifications.filter(n => !n.isPersistent && n.isStacked);
    const visiblePersistent = notifications.filter(n => n.isPersistent && !n.isStacked);
    const stackedPersistent = notifications.filter(n => n.isPersistent && n.isStacked);

    const sortedVisibleTemporary = this.sortByType(visibleTemporary);
    const sortedVisiblePersistent = this.sortByType(visiblePersistent);

    return [...sortedVisibleTemporary, ...sortedVisiblePersistent, ...stackedTemporary, ...stackedPersistent];
  }

  private sortByType(notifications: GlobalNotification[]): GlobalNotification[] {
    return [...notifications].sort((a, b) => this.getTypePriority(b.type) - this.getTypePriority(a.type));
  }

  private getTypePriority(type: NotificationType): number {
    switch (type) {
      case 'success':
        return 4;
      case 'info':
        return 3;
      case 'danger':
        return 2;
      case 'warning':
        return 1;
      default:
        return 0;
    }
  }

  private getOriginalId(id: string): string {
    return id.replace('_stacked', '');
  }
}
